import styled, { keyframes } from 'styled-components';

import { CALIBRATE, CALIBRATE_ANIM, ACQ_MOBILE, NAV_DESKTOP } from '../../../../theme/tokens';
import { pulseCard } from '../../../../theme/mixins';
import { Text } from '../../../../components';

const panelIn = keyframes`
  from { opacity: 0; transform: scale(0.992); }
  to { opacity: 1; transform: scale(1); }
`;

const fadeIn = keyframes`
  from { opacity: 0; }
  to { opacity: 1; }
`;

/** Hard caret blink while a phrase rests (steady while characters are moving). */
const caretBlink = keyframes`
  0%, 46% { opacity: 1; }
  50%, 96% { opacity: 0; }
  100% { opacity: 1; }
`;

/**
 * The big "Calibrating" panel: one rounded, OPAQUE card pinned over the whole Pulse content
 * column (the Pulse page is the positioning context), so it covers every section card. The
 * surface is the shared `pulseCard` mixin (same as the real Pulse sections), and it hosts the
 * depth-racked bokeh + glow waves CLIPPED to itself, with the focus content centred. Catches
 * pointer events so the covered sections are inert.
 */
export const Panel = styled.section`
  ${pulseCard}
  position: absolute;
  inset: 0;
  z-index: 4;
  pointer-events: auto;
  overflow: hidden;
  isolation: isolate;
  border-radius: 16px;
  border: 1px solid ${CALIBRATE.cardHairline};
  box-shadow: 0 28px 64px -30px ${CALIBRATE.cardShadow};
  animation: ${panelIn} ${CALIBRATE_ANIM.enterMs}ms ${CALIBRATE_ANIM.ease} both;

  @media (prefers-reduced-motion: reduce) {
    animation: none;
  }
`;

/**
 * Sticky centring frame for the focus content. Pinned to the top of the scroll-port at a
 * height that matches the visible content area, so the content stays centred in view while
 * the panel (taller than the viewport) holds its place behind. Capped at the panel's own
 * height (`100%`) so a short page centres within it rather than below it.
 */
export const Centerer = styled.div`
  position: sticky;
  top: 0;
  height: min(100dvh, 100%);
  z-index: 2;
  display: grid;
  place-items: center;
  padding: 24px;
  box-sizing: border-box;

  ${NAV_DESKTOP} {
    /* Desktop: the content area is a fixed-height internal scroller; centre within the
       measured content card rather than the full viewport (which includes chrome). */
    height: min(var(--content-height, 100dvh), 100%);
  }
`;

/** Toggle for the glow waves. `opacity: 0` previews the panel WITHOUT the waves; set it to 1
 *  to bring them back. (Kept as a one-line switch so it is trivial to flip.) */
export const Waves = styled.div`
  opacity: 0;
`;

/** Centred content column. Capped narrow so the copy stays a tidy column on the wide panel. */
export const Content = styled.div`
  position: relative;
  z-index: 1;
  width: min(460px, 100%);
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  gap: 20px;
`;

/** Holds the 64px focus mark. */
export const IconWrap = styled.div`
  position: relative;
  display: grid;
  place-items: center;
  width: 64px;
  height: 64px;
`;

/** Title row: a fixed height (about 2 lines) reserved so the card never reflows as the
 *  typewriter swaps phrases of different lengths. */
export const Heading = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  min-height: 60px;
`;

/** The typewriter line. Re-keyed per phrase by the caller so each new phrase fades in (a
 *  gentle cross-fade under reduced motion, invisible mid-type otherwise). */
export const Typed = styled.span`
  display: inline-block;
  font-size: 22px;
  font-weight: 700;
  letter-spacing: -0.01em;
  line-height: 1.32;
  color: ${CALIBRATE.title};
  animation: ${fadeIn} ${CALIBRATE_ANIM.crossfadeMs}ms ${CALIBRATE_ANIM.ease} both;

  ${ACQ_MOBILE} {
    font-size: 20px;
  }
`;

/** Blinking caret after the typed text; steady while characters are moving. Hard on/off blink
 *  (steps ease) so it reads as a real cursor. Hidden under reduced motion (the phrases just
 *  cross-fade then, so there is no typing metaphor for a caret to support). */
export const Caret = styled.span<{ $busy: boolean }>`
  display: inline-block;
  width: 2px;
  height: 1.05em;
  margin-left: 5px;
  vertical-align: -0.16em;
  border-radius: 1px;
  background: ${CALIBRATE.caret};
  animation: ${caretBlink} ${CALIBRATE_ANIM.caretBlinkMs}ms ${CALIBRATE_ANIM.caretEase} infinite;
  ${(p) => (p.$busy ? 'animation: none; opacity: 1;' : '')}

  @media (prefers-reduced-motion: reduce) {
    display: none;
  }
`;

/** Supporting caption beneath the title. Spans the content column (no artificial width cap),
 *  so it stays on one line wherever there is room and only wraps on a narrow (mobile) panel. */
export const Caption = styled(Text)`
  display: block;
  font-size: 13.5px;
  font-weight: 500;
  line-height: 1.5;
  color: ${CALIBRATE.caption};
`;

/** "N days until live data" pill, shown only while the launch-window countdown is running. */
export const DaysChip = styled(Text)`
  display: inline-flex;
  align-items: center;
  margin-top: 2px;
  padding: 7px 14px;
  border-radius: 999px;
  background: ${CALIBRATE.chipBg};
  border: 1px solid ${CALIBRATE.chipBorder};
  color: ${CALIBRATE.chipText};
  font-size: 12.5px;
  font-weight: 600;
  letter-spacing: 0.01em;
`;

/** Visually-hidden, stable heading for assistive tech (the animated line is decorative). */
export const SrOnly = styled(Text)`
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  margin: -1px;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  white-space: nowrap;
  border: 0;
`;
